import {
  Box,
  Fade,
  SimpleGrid,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Fragment, useEffect, useState } from 'react';
import { useExperiencesStore } from '../store/experiences';
import DeleteExperienceModal from '../components/modals/DeleteExperienceModal';
import RenameExperienceModal from '../components/modals/RenameExperienceModal';
import CreateExperienceModal from '../components/modals/CreateExperienceModal';
import { ExperienceListItem, PayloadUploadPart } from '../interfaces';
import { Experiences } from '../Icons/Experiences';
import TopActionBar from '../components/common/TopActionBar';
import ExperiencesBarActions from '../components/barActions/ExperiencesBarActions';
import ExperienceItem from '../components/experiencesView/ExperienceItem';
import CreateExperienceCard from '../components/cards/CreateExperienceCard';
import { useUserStore } from '../store/user';
import configureAxios from '../axiosClient';
import React from 'react';
import ContactModal from '../components/modals/ContactModal';
import UnPublishExperienceModal from '../components/modals/UnPublishExperienceModal';
import UpdateSourceMaterialsModal from '../components/modals/UpdateSourceMaterialsModal';
import { v4 as uuid } from 'uuid';
import CommunityDashboard from '../components/communityView/CommunityDashboard';
import { useNavigate } from 'react-router-dom';
import { NextArrow } from '../Icons/NextArrow';
import { Home } from '../Icons/Home';
import { usePlanStore } from '../store/plan';

const axiosClient = configureAxios();

function ExperiencesView() {
  const toast = useToast({
    position: 'top',
    variant: 'top-accent',
  });
  const navigate = useNavigate();

  const [searchBarText, setSearchBarText] = useState('');
  const [changeInExperience, setChangeInExperience] = useState(false);
  const [experiencesLoaded, setExperiencesLoaded] = useState(true);
  const [renameModalActive, setRenameModalActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [createModalActive, setCreateModalActive] = useState(false);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [contactModalActive, setContactModalActive] = useState(false);
  const [hoveredExperience, setHoveredExperience] = useState<string | null>(
    null
  );
  const [stopFetching, setStopFetching] = useState(false);
  const [updateSourceModalActive, setUpdateSourceModalActive] = useState(false);
  const [totalProgressCompleted, setTotalProgressCompleted] = useState(0);
  const [experiencesToShow, setExperiencesToShow] = useState<
    ExperienceListItem[]
  >([]);

  const [page] = useState(1);
  const [size] = useState(8);

  const {
    setInactiveOrganisation,
    setUserCreateNewOrganisationView,
    organisationList,
  } = useUserStore();

  function debounce<T extends (...args: string[]) => void>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;

    return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  const organisation = useUserStore((state) => state.organisation);
  const {
    filteredExperiences,
    setExperiences,
    setSelectedExperience,
    renameExperience,
    deleteExperience,
    unPublishExperience,
    filterExperiences,
    setShowEmbedModal,
    viewerUnPublishExperienceModalActive,
    setViewerUnPublishExperienceModalActive,
    setShareCommunityExperienceModalActive,
    setCreatorToolDashboard,
  } = useExperiencesStore();
  const { setShowPlans } = usePlanStore();

  const debouncedFilter = debounce(filterExperiences, 500);
  const fetchExperiences = async () => {
    const userOrgId = organisation.organizationId;
    if (userOrgId?.length > 0) {
      setExperiencesLoaded(true);
      try {
        const res = await axiosClient.get(
          `org/${userOrgId}/experiences?type=all&page=${page}&size=${size}`
        );
        const experiencesData = res?.data?.data?.experiences || [];
        const sortedExperiences = experiencesData?.sort(
          (a: ExperienceListItem, b: ExperienceListItem) =>
            a.created &&
            b.created &&
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );
        setExperiencesToShow(sortedExperiences);
        setExperiences(sortedExperiences);
        setExperiencesLoaded(false);
        setChangeInExperience(false);
        sortedExperiences.length === 0
          ? setCreatorToolDashboard(true)
          : setCreatorToolDashboard(false);

        if (
          experiencesData?.length === 0 ||
          experiencesData.every((exp: ExperienceListItem) => exp.processed)
        ) {
          setStopFetching(true);
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    fetchExperiences();

    const intervalId = setInterval(() => {
      fetchExperiences();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [organisation.organizationId, changeInExperience, stopFetching]);

  const openDeleteExperienceModal = (
    experienceListItem: ExperienceListItem
  ) => {
    setSelectedExperience(experienceListItem);
    setDeleteModalActive(true);
  };

  const unPublishExperienceModal = (experienceListItem: ExperienceListItem) => {
    setSelectedExperience(experienceListItem);
    setViewerUnPublishExperienceModalActive(true);
  };

  const shareCommunityExperienceModal = (
    experienceListItem: ExperienceListItem
  ) => {
    setSelectedExperience(experienceListItem);
    setShareCommunityExperienceModalActive(true);
  };

  const openRenameExperienceModal = (
    experienceListItem: ExperienceListItem
  ) => {
    setSelectedExperience(experienceListItem);
    setRenameModalActive(true);
  };

  const openEmbedExperienceModal = (experienceListItem: ExperienceListItem) => {
    setSelectedExperience(experienceListItem);
    setShowEmbedModal(true);
  };

  const searchExperiences = (value: string) => {
    setSearchBarText(value);
    debouncedFilter(value);
  };

  const openUpdateSourceMaterialsModal = (
    experienceListItem: ExperienceListItem
  ) => {
    setSelectedExperience(experienceListItem);
    setUpdateSourceModalActive(true);
  };

  const getS3PresignedUrl = async (
    org_id: string,
    file: File,
    exp_id?: string | undefined
  ): Promise<{
    urls: string[];
    uploadId: string;
    partSize: number;
    payloadId: string;
    expId: string;
  }> => {
    const fileSize = file.size.toString();
    const params = {
      owner: org_id,
      exp_id: exp_id || uuid(),
      payload_id: uuid(),
      content_type: file.type,
      payload_size: fileSize,
    };

    return axiosClient
      .post(
        process.env.REACT_APP_API_URL + 'experience/generate-presigned-urls',
        params
      )
      .then((res) => {
        const data = res.data.data;
        const isStatusOk = res.data.status === 'OK';

        if (isStatusOk) {
          if (!data.exceeds)
            return {
              urls: data.presigned_urls,
              uploadId: data.upload_id,
              partSize: data.part_size,
              payloadId: params.payload_id,
              expId: params.exp_id,
            };
          else {
            toast({
              title: data.message,
              status: 'error',
            });
            return {
              urls: [],
              uploadId: '',
              partSize: 0,
              payloadId: '',
              expId: '',
            };
          }
        } else {
          toast({
            title: data.message,
            status: 'error',
          });
          return {
            urls: [],
            uploadId: '',
            partSize: 0,
            payloadId: '',
            expId: '',
          };
        }
      })
      .catch((err) => {
        console.error(err);
        toast({
          title: err.response.data.data,
          status: 'error',
        });
        return {
          urls: [],
          uploadId: '',
          partSize: 0,
          payloadId: '',
          expId: '',
        };
      });
  };

  const sendPayloadToS3 = async (
    urls: string[],
    partSize: number,
    file: File,
    controller: AbortController | null
  ): Promise<PayloadUploadPart[] | null> => {
    setTotalProgressCompleted(0);
    const totalParts = urls.length;
    const uploadedParts: PayloadUploadPart[] = [];
    let totalProgressCompleted = 0;
    for (let i = 0; i < totalParts; i++) {
      const part = urls[i];
      const partFile = file.slice(i * partSize, (i + 1) * partSize);
      const response = await axiosClient
        .put(part, partFile, {
          signal: controller?.signal,
          onUploadProgress: (ProgressEvent) => {
            if (ProgressEvent.total) {
              const progressCompleted =
                totalProgressCompleted +
                Math.round(
                  (ProgressEvent.loaded * 100) /
                    totalParts /
                    ProgressEvent.total
                );
              setTotalProgressCompleted(progressCompleted);
            }
          },
        })
        .then((res) => {
          return res.status === 200
            ? { ETag: res.headers['etag'] || '', PartNumber: i + 1 }
            : null;
        })
        .catch((err) => {
          if (err.code === 'ERR_CANCELED') {
            console.log('Upload canceled');
            return null;
          }
          console.error(err);
          return null;
        });
      if (!response) {
        return null;
      }
      uploadedParts.push(response);
      totalProgressCompleted = Math.round((100 / totalParts) * (i + 1));
    }
    return uploadedParts;
  };

  const completeMultiPartUpload = async (
    orgId: string,
    expId: string,
    payloadId: string,
    uploadId: string,
    contentType: string,
    uploadedParts: PayloadUploadPart[]
  ) => {
    const params = {
      owner: orgId,
      exp_id: expId,
      payload_id: payloadId,
      content_type: contentType,
      upload_id: uploadId,
      parts: uploadedParts,
    };
    return axiosClient
      .post(
        process.env.REACT_APP_API_URL + 'experience/complete-multipart-upload',
        params
      )
      .then((res) => {
        const data = res.data.data;
        const isStatusOk = res.data.status === 'OK';

        if (isStatusOk) {
          if (!data.exceeds) return true;
          else {
            toast({
              title: data.message,
              status: 'error',
            });
            return false;
          }
        } else {
          toast({
            title: data.message,
            status: 'error',
          });
          return false;
        }
      })
      .catch((err) => {
        console.error(err);
        return false;
      });
  };

  const handleInActiveOrganisation = (org: any) => {
    const inActiveOrg = {
      organizationId: org.org_id,
      alias: org.alias,
      roles: org.roles,
    };

    setInactiveOrganisation(inActiveOrg);
    setUserCreateNewOrganisationView(true);
    setShowPlans(true);
  };

  // useEffect(() => {
  //   handleInActiveOrganisation(organisation);
  // }, []);

  return (
    <Fade in={true} transition={{ enter: { duration: 0.2 } }}>
      <UpdateSourceMaterialsModal
        totalProgressCompleted={totalProgressCompleted}
        abortController={abortController}
        setAbortController={(controller: AbortController) =>
          setAbortController(controller)
        }
        updateSourceModalActive={updateSourceModalActive}
        setUpdateSourceModalActive={(state: boolean) =>
          setUpdateSourceModalActive(state)
        }
        setChangeInExperience={(state) => setChangeInExperience(state)}
        getS3PresignedUrl={(
          org_id: string,
          file: File,
          exp_id?: string | undefined
        ) => getS3PresignedUrl(org_id, file, exp_id)}
        sendPayloadToS3={(
          urls: string[],
          partSize: number,
          file: File,
          controller: AbortController | null
        ) => sendPayloadToS3(urls, partSize, file, controller)}
        completeMultiPartUpload={(
          orgId: string,
          expId: string,
          payloadId: string,
          uploadId: string,
          contentType: string,
          uploadedParts: PayloadUploadPart[]
        ) =>
          completeMultiPartUpload(
            orgId,
            expId,
            payloadId,
            uploadId,
            contentType,
            uploadedParts
          )
        }
      />
      <RenameExperienceModal
        renameModalActive={renameModalActive}
        setRenameModalActive={(state: boolean) => setRenameModalActive(state)}
        renameExperience={(updatedExperience) => {
          renameExperience(updatedExperience);
          setRenameModalActive(false);
        }}
      />
      <UnPublishExperienceModal
        unPublishExperienceModalActive={viewerUnPublishExperienceModalActive}
        setUnPublishExperienceModalActive={(state: boolean) =>
          setViewerUnPublishExperienceModalActive(state)
        }
        unPublishExperience={(experienceId: string) => {
          unPublishExperience(experienceId);
          setViewerUnPublishExperienceModalActive(false);
        }}
        fetchExperiences={fetchExperiences}
      />
      <DeleteExperienceModal
        deleteModalActive={deleteModalActive}
        setDeleteModalActive={(state: boolean) => setDeleteModalActive(state)}
        deleteExperience={(experienceId: string) => {
          deleteExperience(experienceId);
          setDeleteModalActive(false);
        }}
      />
      <CreateExperienceModal
        totalProgressCompleted={totalProgressCompleted}
        createModalActive={createModalActive}
        abortController={abortController}
        setAbortController={(controller: AbortController) =>
          setAbortController(controller)
        }
        setChangeInExperience={(state) => setChangeInExperience(state)}
        setCreateModalActive={(state: boolean) => setCreateModalActive(state)}
        getS3PresignedUrl={(
          org_id: string,
          file: File,
          exp_id?: string | undefined
        ) => getS3PresignedUrl(org_id, file, exp_id)}
        sendPayloadToS3={(
          urls: string[],
          partSize: number,
          file: File,
          controller: AbortController | null
        ) => sendPayloadToS3(urls, partSize, file, controller)}
        completeMultiPartUpload={(
          orgId: string,
          expId: string,
          payloadId: string,
          uploadId: string,
          contentType: string,
          uploadedParts: PayloadUploadPart[]
        ) =>
          completeMultiPartUpload(
            orgId,
            expId,
            payloadId,
            uploadId,
            contentType,
            uploadedParts
          )
        }
      />
      <TopActionBar
        icon={<Home stroke='white' boxSize={[5]} />}
        title='Home'
        showSearchBar={false}
        searchBarPlaceholder='Search Experience Name...'
        rightActions={
          <ExperiencesBarActions
            createExperience={() => {
              const currentOrg = organisationList.find(
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                (org) => org.org_id === organisation?.organizationId
              );

              if (currentOrg?.is_active) {
                setCreateModalActive(true);
              } else {
                handleInActiveOrganisation(organisation);
              }
            }}
          />
        }
        searchBarText={searchBarText}
        onSearchBarTextChange={(value) => searchExperiences(value)}
      />

      <ContactModal
        contactModalActive={contactModalActive}
        setContactModalActive={setContactModalActive}
      />
      <Box display='flex' flexDirection='column' gap={5} mt={5}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          p={['5px 40px 0px 40px']}
        >
          <Box display='flex' alignItems='center' gap={2}>
            <Experiences stroke={'white'} boxSize={5} />
            <Text fontFamily='Metal' fontSize='14px' textTransform='uppercase'>
              Recent Experiences
            </Text>
          </Box>
          <Box display='flex' alignItems='center' gap={10}>
            {experiencesToShow.length > 0 && (
              <Box
                display='flex'
                alignItems='center'
                cursor='pointer'
                gap={2}
                onClick={() => {
                  navigate('/experiences');
                }}
              >
                <Text fontSize='11px'>View All</Text>
                <NextArrow boxSize={5} />
              </Box>
            )}
          </Box>
        </Box>
        {experiencesToShow.length > 0 && (
          <Fragment>
            <Box p={['0 40px']}>
              <SimpleGrid columns={[3, 3, 3, 3, 4]} spacing={[5]}>
                {searchBarText?.length > 0 ? (
                  filteredExperiences?.length > 0 ? (
                    filteredExperiences?.map(
                      (experienceListItem: ExperienceListItem) => (
                        <Skeleton
                          key={experienceListItem.id}
                          isLoaded={!experiencesLoaded}
                          startColor='#29303C'
                          endColor='transparent'
                          borderRadius='16px'
                        >
                          <ExperienceItem
                            key={experienceListItem.id}
                            experienceListItem={experienceListItem}
                            experienceHovered={
                              hoveredExperience === experienceListItem.id
                            }
                            renameExperience={() =>
                              openRenameExperienceModal(experienceListItem)
                            }
                            shareCommunityExperience={() =>
                              shareCommunityExperienceModal(experienceListItem)
                            }
                            unPublishExperience={() =>
                              unPublishExperienceModal(experienceListItem)
                            }
                            deleteExperience={() =>
                              openDeleteExperienceModal(experienceListItem)
                            }
                            openEmbedModal={() =>
                              openEmbedExperienceModal(experienceListItem)
                            }
                            hoverExperience={() =>
                              setHoveredExperience(experienceListItem.id)
                            }
                            unHoverExperience={() => setHoveredExperience('')}
                            updateSourceMaterials={() =>
                              openUpdateSourceMaterialsModal(experienceListItem)
                            }
                          />
                        </Skeleton>
                      )
                    )
                  ) : (
                    <Skeleton
                      isLoaded={!experiencesLoaded}
                      startColor='#29303C'
                      endColor='transparent'
                      borderRadius='16px'
                    >
                      <CreateExperienceCard
                        createExperience={() => setCreateModalActive(true)}
                      />
                    </Skeleton>
                  )
                ) : experiencesToShow?.length > 0 ? (
                  experiencesToShow?.map(
                    (experienceListItem: ExperienceListItem) => (
                      <ExperienceItem
                        key={experienceListItem.id}
                        experienceListItem={experienceListItem}
                        experienceHovered={
                          hoveredExperience === experienceListItem.id
                        }
                        renameExperience={() =>
                          openRenameExperienceModal(experienceListItem)
                        }
                        unPublishExperience={() =>
                          unPublishExperienceModal(experienceListItem)
                        }
                        shareCommunityExperience={() =>
                          shareCommunityExperienceModal(experienceListItem)
                        }
                        deleteExperience={() =>
                          openDeleteExperienceModal(experienceListItem)
                        }
                        openEmbedModal={() =>
                          openEmbedExperienceModal(experienceListItem)
                        }
                        hoverExperience={() =>
                          setHoveredExperience(experienceListItem.id)
                        }
                        unHoverExperience={() => setHoveredExperience('')}
                        updateSourceMaterials={() =>
                          openUpdateSourceMaterialsModal(experienceListItem)
                        }
                      />
                    )
                  )
                ) : (
                  <Skeleton
                    isLoaded={!experiencesLoaded}
                    startColor='#29303C'
                    endColor='transparent'
                    borderRadius='16px'
                  >
                    <CreateExperienceCard
                      createExperience={() => setCreateModalActive(true)}
                    />
                  </Skeleton>
                )}
              </SimpleGrid>
            </Box>
          </Fragment>
        )}
        <CommunityDashboard />
      </Box>
    </Fade>
  );
}

export default ExperiencesView;
